import React, { useMemo } from "react";
import {
  Card,
  Typography,
  Space,
  Tag,
  Alert,
  Divider,
  Button,
  message,
} from "antd";
import { ApiOutlined, LockOutlined, LinkOutlined, CopyOutlined } from "@ant-design/icons";
import { validStatuses } from "../../../utils/bookingStatus";

const { Title, Text, Paragraph } = Typography;

type HttpMethod = "POST";

interface EndpointDoc {
  method: HttpMethod;
  action: string;
  title: string;
  ui: string;
  description: string;
  /** Displayed request example (may include comments). */
  request: string;
  /** Clean JSON body for the sample curl (no comments). */
  curlBody: Record<string, unknown>;
  response?: string;
  notes?: string[];
}

/** Public app alias (Vercel rewrite → Supabase partner-api). */
const PARTNER_API_PUBLIC_ORIGIN = "https://app.efreightforwarding.com";
const PARTNER_API_PUBLIC_PATH = "/api/partner";
const PARTNER_API_DOCS_PATH = "/partner-api";

function isLocalHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/** Partner-facing API URL (no supabase.co in the path). */
export function getPartnerApiBaseUrl(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    const { hostname, origin } = window.location;
    // Local Vite has no rewrite — always show the production public alias.
    if (!isLocalHost(hostname)) {
      return `${origin}${PARTNER_API_PUBLIC_PATH}`;
    }
  }
  return `${PARTNER_API_PUBLIC_ORIGIN}${PARTNER_API_PUBLIC_PATH}`;
}

/** Shareable docs page URL (always production when copying from localhost). */
export function getPartnerApiDocsPublicUrl(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    const { hostname, origin } = window.location;
    if (!isLocalHost(hostname)) {
      return `${origin}${PARTNER_API_DOCS_PATH}`;
    }
  }
  return `${PARTNER_API_PUBLIC_ORIGIN}${PARTNER_API_DOCS_PATH}`;
}

/** Dummy examples only — not real customers. */
const SAMPLE = {
  email: "ops@acme-demo.example",
  phone: "+31000000000",
  companyId: 1001,
  companyName: "Acme Demo BV",
  bookingId: 10042,
  bookingRef: "EFF10042SEA",
  bookingTokenShort: "10042",
  eta: "2026-12-01",
  containerRef: "ABCD1234567",
} as const;

const ENDPOINTS: EndpointDoc[] = [
  {
    method: "POST",
    action: "identity.resolve",
    title: "Resolve identity",
    ui: "Pipeline step 1",
    description:
      "Map an inbound sender (email and/or phone) and/or a booking reference to one or more companies and linked users.",
    curlBody: {
      action: "identity.resolve",
      email: SAMPLE.email,
      phone: SAMPLE.phone,
      q: SAMPLE.bookingRef,
    },
    request: `{
  "action": "identity.resolve",
  "email": "${SAMPLE.email}",
  "phone": "${SAMPLE.phone}",
  "q": "${SAMPLE.bookingRef}"
}

// At least one of email, phone, or q is required.
// q accepts ${SAMPLE.bookingRef}, EFF${SAMPLE.bookingTokenShort}, or ${SAMPLE.bookingTokenShort} — finds the owning company.`,
    response: `{
  "companies": [
    {
      "id": ${SAMPLE.companyId},
      "name": "${SAMPLE.companyName}",
      "match_reasons": ["user_email", "booking_reference"],
      "users": [{ "id": "…", "email": "${SAMPLE.email}" }]
    }
  ],
  "booking": {
    "id": ${SAMPLE.bookingId},
    "eff_reference": "${SAMPLE.bookingRef}",
    "reference": "${SAMPLE.bookingRef}",
    "status": "in_transit",
    "company_id": ${SAMPLE.companyId}
  }
}

// booking is null when q was omitted or no booking matched.`,
    notes: [
      "At least one of email, phone, or q (booking reference) required.",
      "q alone is enough: company comes from the booking’s company_id (match_reason booking_reference).",
      "0 companies → unknown sender; multiple → client must pick (or ask user).",
      "Do not trust a client-supplied company_id without resolve/scope.",
    ],
  },
  {
    method: "POST",
    action: "companies.get",
    title: "Get company",
    ui: "Pipeline step 2 (optional)",
    description:
      "Reload company profile + users when you already know company_id from a prior resolve.",
    curlBody: {
      action: "companies.get",
      company_id: SAMPLE.companyId,
    },
    request: `{
  "action": "companies.get",
  "company_id": ${SAMPLE.companyId}
}`,
    response: `{
  "id": ${SAMPLE.companyId},
  "name": "${SAMPLE.companyName}",
  "status": "active",
  "phone_number": "${SAMPLE.phone}",
  "invoice_email": "${SAMPLE.email}",
  "users": [{ "id": "…", "email": "${SAMPLE.email}" }]
}`,
  },
  {
    method: "POST",
    action: "bookings.lookup",
    title: "Lookup booking",
    ui: "Step 3 · Lookup by id / reference",
    description: `Find one booking for a company from a message token (${SAMPLE.bookingRef}, EFF${SAMPLE.bookingTokenShort}, or ${SAMPLE.bookingTokenShort}).`,
    curlBody: {
      action: "bookings.lookup",
      company_id: SAMPLE.companyId,
      q: SAMPLE.bookingRef,
    },
    request: `{
  "action": "bookings.lookup",
  "company_id": ${SAMPLE.companyId},
  "q": "${SAMPLE.bookingRef}"
}`,
    response: `{
  "booking": { /* full dashboard booking JSON */ }
}
// or { "booking": null } when not found under this company`,
    notes: [
      "Always company-scoped.",
      "Includes timeline, last-mile, master B/L + same-company siblings.",
    ],
  },
  {
    method: "POST",
    action: "bookings.list",
    title: "List bookings",
    ui: "Step 3 · Recent open / All / Filter by status",
    description:
      "List bookings for a company. Use filters to mirror the admin list modes.",
    curlBody: {
      action: "bookings.list",
      company_id: SAMPLE.companyId,
      scope: "open",
      limit: 30,
    },
    request: `{
  "action": "bookings.list",
  "company_id": ${SAMPLE.companyId},
  "scope": "open",
  "status": null,
  "limit": 30
}

// scope: "open" = submitted → delivery_planned; omit scope + status = all
// status: optional single status string (see Statuses below)
// limit: optional, default 30, max 100

// Statuses (status filter):
// ${validStatuses.join(", ")}

// scope "open" includes:
// submitted, approved, waiting_for_goods, goods_received_origin, in_transit,
// customs_pending_clearance, custom_clearance, customs_cleared, fyco,
// out_for_delivery, delivery_planned`,
    response: `{
  "bookings": [
    {
      "id": ${SAMPLE.bookingId},
      "eff_reference": "${SAMPLE.bookingRef}",
      "status": "in_transit",
      "eta": "${SAMPLE.eta}",
      "container_reference": "${SAMPLE.containerRef}"
    }
  ]
}`,
    notes: [
      "List rows are compact; use bookings.get for full JSON.",
      "scope=open is what the demo uses when the message has no booking reference.",
      `All statuses for the status field: ${validStatuses.join(", ")}.`,
    ],
  },
  {
    method: "POST",
    action: "bookings.get",
    title: "Get booking",
    ui: "Step 3 · Click row → full JSON",
    description:
      "Full booking payload (tracking, delivery, docs flags, master container when present).",
    curlBody: {
      action: "bookings.get",
      company_id: SAMPLE.companyId,
      booking_id: SAMPLE.bookingId,
    },
    request: `{
  "action": "bookings.get",
  "company_id": ${SAMPLE.companyId},
  "booking_id": ${SAMPLE.bookingId}
}`,
    response: `{
  "booking": {
    "id": ${SAMPLE.bookingId},
    "eff_reference": "${SAMPLE.bookingRef}",
    "status": "in_transit",
    "container": { … },
    "master_container": {
      "same_company_sibling_bookings": [ … ]
    },
    …
  }
}`,
    notes: [
      "Master B/L data is allowed; sibling bookings are same-company only.",
      "404 if booking missing or belongs to another company.",
    ],
  },
];

function buildSampleCurl(
  baseUrl: string,
  body: Record<string, unknown>,
): string {
  const json = JSON.stringify(body);
  return `curl -X POST '${baseUrl}' \\
  -H 'Authorization: Bearer YOUR_PARTNER_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '${json.replace(/'/g, `'\\''`)}'`;
}

const codeBlockStyle: React.CSSProperties = {
  margin: "4px 0 12px",
  padding: 12,
  background: "#fafafa",
  border: "1px solid #f0f0f0",
  borderRadius: 6,
  fontSize: 12,
  overflow: "auto",
};

const EndpointCard: React.FC<{ doc: EndpointDoc; baseUrl: string }> = ({
  doc,
  baseUrl,
}) => {
  const curl = buildSampleCurl(baseUrl, doc.curlBody);

  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(curl);
      message.success("curl copied");
    } catch {
      message.error("Could not copy");
    }
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space wrap>
          <Tag color="blue">{doc.method}</Tag>
          <Text code style={{ fontSize: 12 }}>
            {baseUrl}
          </Text>
          <Tag>{doc.action}</Tag>
        </Space>
      }
      extra={<Tag>{doc.ui}</Tag>}
    >
      <Text strong>{doc.title}</Text>
      <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 12 }}>
        {doc.description}
      </Paragraph>

      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 4 }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Sample curl
        </Text>
        <Button size="small" icon={<CopyOutlined />} onClick={copyCurl}>
          Copy curl
        </Button>
      </Space>
      <pre style={codeBlockStyle}>{curl}</pre>

      <Text type="secondary" style={{ fontSize: 12 }}>
        Request body
      </Text>
      <pre style={codeBlockStyle}>{doc.request}</pre>

      {doc.response && (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Response
          </Text>
          <pre style={codeBlockStyle}>{doc.response}</pre>
        </>
      )}

      {doc.notes && doc.notes.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {doc.notes.map((note) => (
            <li key={note}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {note}
              </Text>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

interface PartnerApiDocsProps {
  publicPage?: boolean;
}

/** Partner data API docs. */
export const PartnerApiDocs: React.FC<PartnerApiDocsProps> = ({
  publicPage = false,
}) => {
  const baseUrl = useMemo(() => getPartnerApiBaseUrl(), []);
  const docsUrl = useMemo(() => getPartnerApiDocsPublicUrl(), []);

  const copyText = async (text: string, okMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(okMsg);
    } catch {
      message.error("Could not copy");
    }
  };

  return (
    <div
      style={{
        maxWidth: publicPage ? 920 : undefined,
        margin: publicPage ? "0 auto" : undefined,
        width: "100%",
      }}
    >
      <Card
        title={
          <Space>
            <ApiOutlined />
            <span>Partner data API</span>
            {!publicPage && (
              <Tag icon={<LockOutlined />}>Live (after deploy + migration)</Tag>
            )}
          </Space>
        }
        extra={
          <Button
            icon={<LinkOutlined />}
            onClick={() => copyText(docsUrl, "Public docs link copied")}
          >
            Copy public link
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Paragraph style={{ marginBottom: 8 }}>
          Read-only endpoints to pull company and booking data from the eFreight
          dashboard. For external Gmail / WhatsApp integrations —{" "}
          <Text strong>not</Text> an LLM API.
        </Paragraph>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Single public URL for all actions — JSON body with an{" "}
          <Text code>action</Text> field. Auth with a partner key (
          <Text code>effp_…</Text>), never customer <Text code>eff_</Text> keys.
        </Paragraph>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Public endpoint"
          description={
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text code copyable={{ text: baseUrl }} style={{ fontSize: 12 }}>
                {baseUrl}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                POST this URL for every action below (
                <Text code>identity.resolve</Text>,{" "}
                <Text code>bookings.*</Text>, …).
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Public docs (share this):{" "}
                <Text code copyable={{ text: docsUrl }}>
                  {docsUrl}
                </Text>
              </Text>
            </Space>
          }
        />

        <Alert
          type="warning"
          showIcon
          message="Auth"
          description={
            <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
              {`Authorization: Bearer <partner_api_key>
Content-Type: application/json

Key format: effp_<uuid>_<64-char-hex>`}
            </pre>
          }
        />
      </Card>

      <Title level={5} style={{ marginTop: 0 }}>
        Actions
      </Title>

      {ENDPOINTS.map((doc) => (
        <EndpointCard key={doc.action} doc={doc} baseUrl={baseUrl} />
      ))}

      <Divider />

      <Card size="small" title="Suggested flow">
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          <Text code>identity.resolve</Text> → pick company if needed →{" "}
          <Text code>bookings.lookup</Text> if message has EFF…, else{" "}
          <Text code>bookings.list</Text> with{" "}
          <Text code>{`{ "scope": "open" }`}</Text> →{" "}
          <Text code>bookings.get</Text> when the user picks one.
        </Paragraph>
      </Card>
    </div>
  );
};
